import { useMemo } from 'react'
import { useAuthStore } from '@/stores/authStore'
import type { UserRole } from '@/types/auth'
import { specialistRolesOf } from '@/types/auth'

/**
 * Typed capability keys. Components call useCapability('approvals:decide') — never
 * inspect role strings inline. Adding a new capability = add it here + wire it in
 * deriveCapabilities below.
 */
export type Capability =
  | 'approvals:read'
  | 'approvals:decide'
  // approvals:decide_platform — decide requests on the PLATFORM approval
  // queue (/platform/approvals, mounted under RequirePlatformAdmin; the
  // platform decide routes accept platform_admin). Distinct from
  // approvals:decide, which stays the TENANT gate (a platform_admin does
  // not decide tenant approvals from the tenant queue — see the
  // platform_admin test block). LIVE-32 (2026-09-24): without this key the
  // platform queue had NEVER rendered Approve/Deny for a pure platform_admin.
  | 'approvals:decide_platform'
  | 'approvals:toggle_enforce'
  | 'approvals:revoke'
  | 'approvals:cancel'
  // approvals:manage_second_eye — toggle the per-scope "second-eye"
  // (separation-of-duties) control in Approval Gates settings
  // (docs/governance/SECOND-EYE-SOD.md). Mirrors the backend's
  // second-eye-only PUT gate (putThresholdsCallerAuthorized,
  // cmd/api-gateway/approval_thresholds_handler.go): tenant_owner,
  // tenant_admin, OR approval_approver — a WIDER set than
  // approvals:toggle_enforce (owner-only), because toggling second-eye
  // alone is deliberately held to the same role set as deciding approvals,
  // not the stricter owner/platform_admin gate that governs enforce_mode
  // and the risk-threshold/action-override fields on the same settings
  // page.
  | 'approvals:manage_second_eye'
  // approvals:manage_notify_gate — edit the automated notify-send approval
  // gate (feat/on-outcome-notify-approval-gate: require_approval toggle +
  // channel_ids multi-select) in Approval Gates settings. Mirrors the
  // backend write gate on that field: tenant_owner / platform_admin — the
  // SAME tier as platform_approval_threshold/action_overrides/enforce_mode,
  // deliberately narrower than approvals:manage_second_eye (which also
  // admits tenant_admin/approval_approver).
  | 'approvals:manage_notify_gate'
  | 'users:invite'
  | 'users:change_role'
  | 'users:remove'
  | 'users:grant_owner'
  | 'users:revoke_session'
  | 'guardrails:read'
  | 'guardrails:manage'
  // guardrails:view_violations — view raw violation evidence (message content,
  // session_id, user_id) and bulk-export CSV/JSON from the Violations tab.
  // Coordinated with the backend constant of the same name. The /governance/
  // guardrails route already sits behind RequireRole(owner/admin/operator/
  // platform_admin); this narrows further — same admin set as
  // guardrails:manage, excluding tenant_operator, since evidence is PII-bearing.
  | 'guardrails:view_violations'
  // guardrails:export_violations — the Violations tab's Export CSV/JSON buttons,
  // which hit GET /guardrails/violations/export. That route is export:guardrail,
  // held by tenant_owner and platform_admin ONLY — a STRICTER set than
  // view_violations:guardrail, which tenant_admin also holds.
  // Split out because both buttons sat under guardrails:view_violations, so a
  // tenant_admin saw Export and got a 403. guardrail/export_realauthz_test.go
  // already asserted that denial server-side; nothing checked the UI against it.
  | 'guardrails:export_violations'
  | 'billing:read'
  // cost:export — the report API's WRITES (POST /tenant/reports, DELETE
  // /tenant/reports/{id}, and the schedule CRUD), which report/handler.go
  // gates on export:cost, NOT on read:billing. Deliberately separate from
  // billing:read because the SERVER distinguishes them.
  //
  // No user was ever shown a 403 Generate button by the old single gate: FE
  // billing:read was owner/admin, and both also held the write permission. (An
  // earlier version of this comment claimed otherwise.) The defect the split
  // fixes is server-side — one read capability could submit and delete report
  // jobs — and this key keeps the UI honest about which permission each control
  // needs. See the note at the `canWrite` read in ReportsPage for why the two
  // predicates coincide today.
  | 'cost:export'
  // workflow:list — populate a workflow picker. FIVE tenant roles carry no
  // "workflow" key in rbac.rego and are denied fail-closed: the three tenant
  // finance roles plus approval_viewer and approval_approver. Gates the Reports
  // page's Advanced workflow filter and CostDashboard's name lookup, both of
  // which 403'd for those roles.
  | 'workflow:list'
  // license:read — the licence/consumption endpoints (/licenses/tenant/{id}/info,
  // /tiers, /licenses/execution-usage, /licenses/execution-bundles). rbac.rego
  // gives tenant_owner/admin/operator read on "license"; tenant_viewer's grant is
  // an EXPLICIT empty list and the three tenant finance roles have no key at all,
  // so all four are denied fail-closed. Panels that call these must be gated, or
  // they render an error card for every one of those roles.
  | 'license:read'
  // prompts:write / agents:write / skills:write — authoring the AI and agent
  // assets on the Build and AI hubs. rbac.rego gives create/update/delete on
  // ai_prompt, agent_definition and agent_skill to platform_admin, tenant_owner
  // and tenant_admin only; tenant_operator and tenant_viewer hold ["read",
  // "list"]. Those routes carry no RequireRole — TenantOnly admits every tenant
  // role — so "New Prompt", "New Agent" and "Create Skill" were shown to roles
  // the server then refuses. The create action is gated on these.
  | 'prompts:write'
  | 'agents:write'
  | 'skills:write'
  // automation:read — the Monitor hub's federated runs (/tenant/automations/runs,
  // /summary). NOT an OPA resource: cmd/api-gateway/routes.go guards the group with
  // RequireRoleForResource("automation", tenant_owner, tenant_admin,
  // tenant_operator, tenant_viewer, platform_admin) — a MOUNT-LEVEL role allowlist
  // that sits above OPA, so no rego-based check can see it. Mirror that list here
  // and keep the two in step.
  | 'automation:read'
  // compliance:read — view compliance evidence packs and Art.22 coverage.
  // Mirrors the Art.22 server-side gate: tenant_owner / tenant_admin / platform_admin.
  | 'compliance:read'
  // dsr:read — the tenant's data-subject request history (GET /tenant/dsr).
  // Mirrors the handler's role check (internal/api/v1/sso/dsr_handler.go
  // checkPermission): tenant_owner / tenant_admin / platform_admin. A role check,
  // not an OPA grant, so this is exact for custom roles too (they never pass).
  | 'dsr:read'
  // art22:manage — declare a definition consequential and record acknowledgments.
  // Same role set as compliance:read (all Art.22 write routes require admin).
  | 'art22:manage'
  // compliance:review — approve/reject a compliance catalog control→data
  // mapping (POST /tenant/compliance/catalogs/{fw}/controls/{id}/review).
  // Mirrors the server-side compliance_evidence:update grant: tenant_owner /
  // tenant_admin / platform_admin. Custom roles cannot review mappings.
  | 'compliance:review'
  // trust:read — view agent trust scores and earned-autonomy tiers.
  // Mirrors the server-side gate: tenant_owner / tenant_admin / platform_admin.
  | 'trust:read'
  // trust:manage — trigger recompute + confirm-tier (mutation routes).
  // Same role set as trust:read — kept separate so gating is semantically explicit.
  | 'trust:manage'
  // billing:write — two DIFFERENT server gates sit behind this one FE key, and
  // they do not have the same holders:
  //   PreflightPolicySetting -> PUT /costs/preflight-policy
  //        -> update:budget  (owner, admin, billing_admin)
  //   ChargebackPage "Correct a cost" -> POST /costs/corrections
  //        -> manage:billing (owner ONLY — rbac.rego's only "manage" on billing
  //           is tenant_owner's, at :603)
  // The FE narrows to tenant_owner: correct for corrections, stricter than the
  // server for the preflight policy. Collapsing two gates onto one key is the same
  // conflation the report split removed; it is left as-is only because owner-only
  // is the safe side of both. Do NOT widen this key without splitting it first.
  // (An earlier version of this comment said the gate "allows tenant_owner and
  // tenant_billing_admin" — true of update:budget, false of manage:billing. It also
  // justified the narrowing by claiming the auth store "does not surface
  // tenant_billing_admin". That is false: the role is
  // assignable to a real tenant user — user/handler.go's isValidRole accepts it and
  // migration 000091 widened the DB CHECK — and authStore passes role through
  // untouched. The narrowing is a deliberate conservative choice, not a limitation.)
  | 'billing:write'
  | 'cost_center:manage'
  | 'budget:manage'
  | 'alert:acknowledge'
  // custom_role:manage — create/update/delete custom roles in this tenant.
  // Mirrors the OPA custom_role grant exactly: tenant_owner, tenant_admin.
  // NOT platform_admin, which holds no custom_role grant.
  | 'custom_role:manage'
  // sensitive_fields:manage — create/update custom sensitive-field definitions.
  // Mirrors the backend adminRoles gate on POST/PUT: tenant_owner, tenant_admin,
  // platform_admin (internal/api/v1/sensitivefield/handler.go). Read access is
  // gated at the route level (App.tsx RequireRole = backend readRoles), so no
  // separate read capability is needed here.
  | 'sensitive_fields:manage'
  // ai_provider_offer:accept — accept or decline a Vharta platform AI
  // credential offer and its per-token rate card (ADR-236 §1 rule 3, §7).
  // tenant_owner / tenant_admin ONLY. Deliberately NOT platform_admin, even
  // while impersonating: the party making the offer must not be able to
  // accept it (the server explicitly denies platform personas too). Do not
  // swap this for usePersona().canManageTenantResources, which includes
  // platform_admin.
  | 'ai_provider_offer:accept'
  // council:override_vote — DISPLAY ONLY (issue #268 §2.3/§4.4). Mirrors the
  // default RBAC grant (tenant_owner/tenant_admin) for the new, custom-role-
  // delegable `override` action on the council_session resource. The real
  // gate for the "Override human seat" button is the server-computed
  // CouncilSession.viewerCanOverride field (it uses CheckPermission, which
  // sees custom-role grants this role-string-only hook cannot) — never gate
  // council vote/override UI on this capability. Use it only for things like
  // a settings-page "can this role override council votes?" preview.
  | 'council:override_vote'

export type CapabilitySet = Record<Capability, boolean>

/**
 * Role→capability mapping — THE single source of truth.
 *
 * Backend OPA grants (verified against OPA policy):
 *   approvals:decide        — tenant_owner, tenant_admin, approval_approver
 *   approvals:decide_platform — platform_admin (platform approval queue; LIVE-32)
 *   approvals:read+list     — the above three + tenant_operator, tenant_viewer, approval_viewer
 *   approvals:toggle_enforce— tenant_owner only (enforce-mode toggle in Approval Gates settings)
 *   approvals:manage_second_eye — tenant_owner, tenant_admin, approval_approver (second-eye
 *                             per-scope toggle in Approval Gates settings; deliberately wider
 *                             than approvals:toggle_enforce — see the Capability union doc comment)
 *   approvals:revoke        — tenant_owner, platform_admin (withdraw an already-approved grant)
 *   approvals:cancel        — tenant_owner, tenant_admin, approval_approver (cancel pending; also
 *                             the requester may cancel their own pending request regardless of role)
 *   users:invite            — tenant_owner, tenant_admin  (USER_CREATE in ROLE_PERMISSIONS)
 *   users:change_role       — tenant_owner, tenant_admin  (USER_UPDATE in ROLE_PERMISSIONS)
 *   users:remove            — tenant_owner, tenant_admin  (USER_DELETE in ROLE_PERMISSIONS)
 *   users:grant_owner       — tenant_owner only           (SoD: only owner can promote to owner)
 *   users:revoke_session    — tenant_owner, tenant_admin
 *   guardrails:read         — any tenant role (GET /tenant/guardrails is open)
 *   guardrails:manage       — tenant_owner, tenant_admin (GR.W1: PUT is RBAC-gated
 *                             to tenant_owner/tenant_admin/platform_admin server-side)
 *   guardrails:view_violations — tenant_owner, tenant_admin, platform_admin
 *                             (raw evidence + export; excludes tenant_operator,
 *                             who may still view the Guardrails/Policies tabs)
 *
 * approval_approver and approval_viewer are composable specialised roles that
 * may be held alongside a base tenant role (e.g. tenant_viewer + approval_approver).
 * User.role carries only the PRIMARY role, so this function is called once per
 * role the caller holds and the results are OR'd — see unionCapabilities and
 * useCapabilities below. That is the "if the backend later returns multiple
 * roles" case this comment used to anticipate; the payload now carries
 * additionalRoles, so it is no longer hypothetical.
 *
 * Why it matters, concretely: without the union, an operator who also holds
 * approval_approver reached the Approval Gates page (the route and nav tab
 * resolve through hasRole, which does union) and then found the second-eye
 * toggle DISABLED, because approvals:manage_second_eye derives from the primary
 * role alone and is not in SERVER_ANSWERABLE. The door opened and the room
 * stayed locked.
 */
function deriveCapabilities(role: UserRole | null | undefined): CapabilitySet {
  // approvals:decide — tenant admins + the dedicated approver specialised role
  const decide =
    role === 'tenant_owner' ||
    role === 'tenant_admin' ||
    role === 'approval_approver'

  // approvals:read — everyone who can decide + read-only tenant roles + approval_viewer
  const read =
    decide ||
    role === 'tenant_operator' ||
    role === 'tenant_viewer' ||
    role === 'approval_viewer'

  // approvals:revoke — withdraw an already-approved grant; privileged owners only
  const revoke = role === 'tenant_owner' || role === 'platform_admin'

  // approvals:decide_platform — platform_admin only (LIVE-32, see the Capability union)
  const platformDecide = role === 'platform_admin'

  // approvals:cancel — cancel a pending request (admin/approver); tenant_owner is
  // already covered by decide. The requester may also cancel their OWN pending
  // requests — that requester path is checked at the component level (isSelfRequest)
  // because it is request-scoped, not role-scoped.
  const cancel = decide

  // users management — only tenant_owner / tenant_admin
  const manageUsers = role === 'tenant_owner' || role === 'tenant_admin'
  const isOwner = role === 'tenant_owner'

  return {
    'approvals:read': read,
    'approvals:decide': decide,
    'approvals:decide_platform': platformDecide,
    'approvals:toggle_enforce': isOwner,
    'approvals:revoke': revoke,
    'approvals:cancel': cancel,
    // approvals:manage_second_eye — same role set as approvals:decide (see
    // the Capability union's doc comment for why this is deliberately
    // wider than approvals:toggle_enforce).
    'approvals:manage_second_eye': decide,
    // approvals:manage_notify_gate — tenant_owner/platform_admin, the same
    // tier as platform_approval_threshold (see Capability union doc comment).
    'approvals:manage_notify_gate': isOwner || role === 'platform_admin',
    'users:invite': manageUsers,
    'users:change_role': manageUsers,
    // Owner-only, NOT manageUsers. rbac.rego gives tenant_admin
    // user:["create","read","update","list"] with no "delete" — and says so
    // in words ("Tenant admin … cannot delete tenant or remove users") — while
    // tenant_owner has delete. DELETE /tenant/users/{id} is gated on
    // ActionDelete with no resource-ID extractor, so it refuses an admin for
    // EVERY target. Granting this to admins put a "Remove from Tenant" item on
    // every row that always failed with a bare "permission denied".
    'users:remove': isOwner,
    'users:grant_owner': isOwner,
    'users:revoke_session': manageUsers,
    // guardrails:read — the four tenant core roles plus platform_admin, which is
    // exactly who holds list/read:guardrail in rbac.rego.
    // This was keyed on `read` (the APPROVALS read set), which also includes
    // approval_approver and approval_viewer. Neither holds any guardrail grant,
    // and guardrail/handler.go:134-137 gates every route on list/read:guardrail,
    // so the value was simply wrong. LATENT, not user-visible: nothing in
    // web/src reads this key today (only comments and test mocks), so no 403
    // control was ever rendered from it. Corrected so a future consumer inherits
    // the right set. The old comment claimed "GET is open to any authenticated
    // tenant member" — it is not, and that is what made the wrong set look fine.
    'guardrails:read':
      manageUsers ||
      role === 'tenant_operator' ||
      role === 'tenant_viewer' ||
      role === 'platform_admin',
    // guardrails:manage — mirrors the GR.W1 server-side RBAC gate on PUT
    // (tenant_owner / tenant_admin / platform_admin).
    'guardrails:manage': manageUsers || role === 'platform_admin',
    // guardrails:view_violations — raw evidence (message content, session_id,
    // user_id) + CSV/JSON export. Same admin set as guardrails:manage.
    'guardrails:view_violations': manageUsers || role === 'platform_admin',
    // export:guardrail is owner + platform_admin, NOT tenant_admin — see the
    // Capability union. Keep this narrower than view_violations.
    'guardrails:export_violations': isOwner || role === 'platform_admin',
    // billing:read — the report API's READS plus GET /costs/billing-profile
    // (tax/legal metadata). Mirrors rbac.rego's read:billing holders, intersected
    // with the roles that can reach a TENANT route at all: tenant_owner,
    // tenant_admin and tenant_billing_admin. billing_admin was missing, which hid
    // the whole Reports page from the one role whose job it is — the inverse of the
    // dead-control bug: a capability the server grants, withheld by the UI.
    // tenant_finance_viewer is correctly absent — it holds no read:billing, because
    // that capability also opens the tax profile (see rbac.rego's note on the role).
    'billing:read': manageUsers || role === 'tenant_billing_admin',
    // cost:export — the report API's writes, plus AutomationsPage's chargeback CSV.
    // Same three roles as billing:read today; keyed separately because the SERVER
    // sets differ (billing:read is in custom_role_resource_map, export:cost is not),
    // so a delegated reader is distinguishable at the API. Not in this hook, which
    // is role-string-only and never sees custom-role grants.
    'cost:export': manageUsers || role === 'tenant_billing_admin',
    // license:read — see the Capability union. tenant_viewer is excluded: its
    // "license" grant in rbac.rego is an explicit empty list.
    'license:read': manageUsers || role === 'tenant_operator' || role === 'platform_admin',
    // Authoring AI/agent assets. Mirrors create/update/delete on ai_prompt,
    // agent_definition and agent_skill in rbac.rego: platform_admin plus
    // tenant_owner/tenant_admin. tenant_operator and tenant_viewer are read-only
    // on all three, and the other platform roles hold read/list at most.
    'prompts:write': manageUsers || role === 'platform_admin',
    'agents:write': manageUsers || role === 'platform_admin',
    'skills:write': manageUsers || role === 'platform_admin',
    // automation:read — mirrors the RequireRoleForResource("automation") mount
    // allowlist in routes.go exactly. Includes tenant_viewer, unlike license:read.
    'automation:read':
      manageUsers ||
      role === 'tenant_operator' ||
      role === 'tenant_viewer' ||
      role === 'platform_admin',
    // workflow:list — see the Capability union for which roles lack the grant.
    'workflow:list':
      manageUsers ||
      role === 'tenant_operator' ||
      role === 'tenant_viewer' ||
      role === 'platform_admin',
    // compliance:read — evidence packs + Art.22 coverage (GET endpoints).
    // Mirrors the art22IsAdmin gate: tenant_owner / tenant_admin / platform_admin.
    'compliance:read': manageUsers || role === 'platform_admin',
    // dsr:read — see the Capability union.
    'dsr:read': manageUsers || role === 'platform_admin',
    // art22:manage — declare + acknowledge Art.22 (PUT/POST endpoints).
    // Same gate as compliance:read in v1.
    'art22:manage': manageUsers || role === 'platform_admin',
    // compliance:review — human/GRC review of catalog mappings. Same trio as
    // compliance:read (rbac.rego compliance_evidence: [read, update]).
    'compliance:review': manageUsers || role === 'platform_admin',
    // trust:read — view agent trust scores (GET endpoints).
    // Mirrors the server-side gate: tenant_owner / tenant_admin / platform_admin.
    'trust:read': manageUsers || role === 'platform_admin',
    // trust:manage — recompute + confirm-tier mutations. Same role set.
    'trust:manage': manageUsers || role === 'platform_admin',
    // billing:write — two server gates with different holders behind one key:
    // PUT /costs/preflight-policy (update:budget — owner/admin/billing_admin) and
    // POST /costs/corrections (manage:billing — owner only). Narrowed to owner,
    // the safe side of both. See the Capability union above before widening.
    // (This previously said billing_admin "is not surfaced in the single-role auth
    // model" and "bypasses the FE fieldset-disable". Both false: the role is
    // assignable and authStore passes it through.)
    // Owner only. platform_admin was here and holds NO "billing" key in
    // rbac.rego, so manage:billing is denied for it — it was offered "Correct a
    // cost" and the preflight-policy editor, both of which 403.
    'billing:write': isOwner,
    // cost_center:manage — create/edit/archive/trash cost centres.
    // Mirrors rbac.rego's cost_center CRUD grant exactly: tenant_owner,
    // tenant_admin, platform_admin and tenant_billing_admin. Added because
    // CostCentersPage rendered these controls unconditionally, so every
    // tenant role saw Create/Edit/Archive and every click 403'd.
    'cost_center:manage':
      isOwner ||
      role === 'tenant_admin' ||
      role === 'platform_admin' ||
      role === 'tenant_billing_admin',
    // budget:manage — gates CostDashboard's "Create budget", which used to render
    // unconditionally and 403 for every read-only role.
    // NARROWER than rbac.rego's create:budget/update:budget holders: that set also
    // includes platform_finance_admin (:534), omitted here because this hook keys
    // off a single tenant role string and platform finance personas do not use
    // tenant pages. If one ever does, the server is authoritative and the control
    // is merely absent — under-granting, not over-granting.
    // Pinned both ways in useCapabilities.test.ts; keep it a separate key from
    // cost_center:manage even though the sets match today.
    'budget:manage':
      isOwner ||
      role === 'tenant_admin' ||
      role === 'platform_admin' ||
      role === 'tenant_billing_admin',
    // alert:acknowledge — gates AlertBanner's Acknowledge. Roles with alert
    // read/list but not update:alert can see an alert and not act on it; that
    // already applied to tenant_operator and tenant_viewer before this branch.
    // NARROWER than update:alert's holders, which number seven: four in rbac.rego
    // plus platform_sre, platform_app_operator and platform_on_call in
    // operations_rbac.rego. Omitted for the same reason as budget:manage above.
    'alert:acknowledge':
      isOwner ||
      role === 'tenant_admin' ||
      role === 'platform_admin' ||
      role === 'tenant_billing_admin',
    // custom_role:manage — create/update/delete custom roles in this tenant.
    // Mirrors the OPA custom_role grant: tenant_owner, tenant_admin, platform_admin.
    // tenant_owner / tenant_admin only — the exact custom_role CRUD grant in
    // rbac.rego. platform_admin was ORed in while holding no custom_role grant.
    // LATENT, not user-visible: nothing reads this key yet (RolesPage.tsx carries
    // a TODO to adopt it), so no 403 control was rendered. Corrected now so the
    // eventual consumer inherits the right set. The key's own doc comment already
    // named the correct set.
    'custom_role:manage': manageUsers,
    // sensitive_fields:manage — mirrors the backend adminRoles gate on POST/PUT
    // (tenant_owner / tenant_admin / platform_admin).
    'sensitive_fields:manage': manageUsers || role === 'platform_admin',
    // ai_provider_offer:accept — tenant_owner / tenant_admin only; never a
    // platform persona (ADR-236 — see the Capability union doc comment).
    'ai_provider_offer:accept': manageUsers,
    // council:override_vote — DISPLAY ONLY, mirrors the default RBAC grant
    // (tenant_owner/tenant_admin/platform_admin). See the Capability union
    // doc comment: never gate the actual Override human seat button on this.
    'council:override_vote': manageUsers || role === 'platform_admin',
  }
}

/**
 * Returns a stable CapabilitySet object derived from the current user's role.
 * Re-renders only when the role changes.
 *
 * @example
 * ```tsx
 * const caps = useCapabilities()
 * if (!caps['approvals:decide']) return null
 * ```
 */
/**
 * Capability keys the SERVER's vocabulary contains, so its answer can be
 * trusted for them. 18 of this file's 39 capabilities.
 *
 * Both numbers were stale ("17 of 38"), and the pair was load-bearing: an
 * estimate of how many picker toggles change nothing in the console was
 * derived as 39 minus 17, which is wrong twice over. These, and the "other
 * N" below, are now pinned by a test that DERIVES them at runtime
 * (useCapabilities.parity.test.ts) rather than re-counting by hand.
 *
 * WHY THIS LIST EXISTS, and why the server's set is not simply adopted
 * wholesale: the two vocabularies only partly overlap.
 * rbac.EffectiveCapabilities emits 35 keys; 21 of the capabilities THIS file
 * gates on are not among them — license:read, workflow:list,
 * approvals:decide_platform, cost:export, guardrails:export_violations and so
 * on. Treating "absent from the server's list" as "denied" would turn all 21
 * off for every user, including owners.
 *
 * So the rule is per-key: where the server has an opinion, it wins (that is
 * what makes custom roles visible at all); where it has none, the static table
 * below still decides. An empty or missing payload means "the server did not
 * tell us" — the resolver is nil-safe and fails soft — so it must fall back,
 * never deny.
 *
 * Widen this list only by also widening rbac's capability catalog, and prove
 * the overlap rather than assuming it: useCapabilities.serverCaps.test.ts
 * pins this set against the Go catalog.
 *
 * WHAT THE OTHER 21 ACTUALLY ARE, measured rather than guessed, because
 * "21 capabilities still broken" overstates it badly:
 *
 *   18 are NOT in role.capabilityManifestSource at all, so NO custom role can
 *      grant or revoke them. The static table below is the only possible
 *      source and is therefore CORRECT for them — license:read, workflow:list,
 *      cost:export, guardrails:export_violations, approvals:decide_platform
 *      and so on. Nothing to fix.
 *
 *    3 ARE grantable — approvals:toggle_enforce, users:grant_owner,
 *      users:revoke_session — but rbac's BASE-role mapping emits none of them
 *      (verified: zero occurrences each in effective_capabilities.go). So the
 *      payload cannot distinguish "the base role holds it" from "no custom
 *      role granted it", and overlaying them would set them false for every
 *      user without a custom role — revoking them from owners. They stay out
 *      until the Go base mapping emits them, which is a policy change that has
 *      to agree with rego; one of the three is documented "owner-only and
 *      non-delegable", so it is a question for a policy owner, not an edit.
 */
const SERVER_ANSWERABLE: readonly Capability[] = [
  'approvals:cancel',
  'art22:manage',
  'approvals:decide',
  'approvals:read',
  'approvals:revoke',
  'billing:read',
  'billing:write',
  'compliance:read',
  'council:override_vote',
  'custom_role:manage',
  'guardrails:manage',
  'guardrails:read',
  'guardrails:view_violations',
  'trust:manage',
  'trust:read',
  'users:change_role',
  'users:invite',
  'users:remove',
]

/**
 * Overlay the server's effective capabilities onto the role-derived set.
 *
 * This is what makes a CUSTOM ROLE visible to the UI. Every gating shape in
 * this app resolved from `user.role` alone, so a custom role rendered its BASE
 * role's surface: a capability the custom role granted beyond the base was
 * hidden even though the server honoured it, and one it revoked below the base
 * still rendered and then 403'd.
 *
 * Exported for unit testing without React context.
 */
export function applyServerCapabilities(
  derived: CapabilitySet,
  serverCaps: readonly string[] | undefined,
): CapabilitySet {
  // Absent/empty = "unknown", so keep the derived answers untouched.
  if (!serverCaps || serverCaps.length === 0) return derived

  const held = new Set(serverCaps)
  const out = { ...derived }
  for (const cap of SERVER_ANSWERABLE) {
    out[cap] = held.has(cap)
  }
  return out
}

/**
 * unionCapabilities ORs two capability sets. Capabilities only ever ACCUMULATE
 * across the roles a user holds — holding approval_viewer in addition to
 * tenant_operator cannot take anything away — so a plain OR is the correct
 * combination, not a precedence rule.
 */
export function unionCapabilities(a: CapabilitySet, b: CapabilitySet): CapabilitySet {
  const out = { ...a }
  for (const key of Object.keys(b) as Capability[]) {
    out[key] = a[key] || b[key]
  }
  return out
}

export function useCapabilities(): CapabilitySet {
  const role = useAuthStore((s) => s.user?.role)
  const serverCaps = useAuthStore((s) => s.user?.capabilities)
  const additionalRoles = useAuthStore((s) => s.user?.additionalRoles)
  // A joined string, not the array: the array identity changes on every store
  // read and would defeat the memo. Same reasoning as usePersona's key.
  const personaKey = specialistRolesOf(additionalRoles).join(',')

  return useMemo(() => {
    const personas = personaKey ? (personaKey.split(',') as UserRole[]) : []
    const derived = personas.reduce(
      (acc, persona) => unionCapabilities(acc, deriveCapabilities(persona)),
      deriveCapabilities(role),
    )
    // The server overlay still runs LAST and still wins for every
    // SERVER_ANSWERABLE key, so this cannot grant beyond what the server says
    // for those; it only fixes the keys the server does not answer.
    return applyServerCapabilities(derived, serverCaps)
    // personaKey MUST be here: a user can gain or lose a persona without their
    // primary role changing.
  }, [role, personaKey, serverCaps])
}

/**
 * Convenience hook — returns a single boolean for a named capability.
 * Prefer useCapabilities() when checking multiple caps in one component.
 */
export function useCapability(cap: Capability): boolean {
  const caps = useCapabilities()
  return caps[cap]
}

// Exported for unit testing without React context.
export { deriveCapabilities }
